import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createTranslator, isLocale, negotiateLocale, type Locale, type MessageValues,
} from '@bzapper/i18n';
import { apiFetch } from './api';

const KEY = 'bz_locale';

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  /** Tradutor com namespace "admin." automático: t('nav.numbers'). */
  t: (key: string, values?: MessageValues) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

function initialLocale(): Locale {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
  if (saved && isLocale(saved)) return saved;
  return negotiateLocale(typeof navigator !== 'undefined' ? navigator.language : null);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = (l: Locale) => {
    localStorage.setItem(KEY, l);
    setLocaleState(l);
    // Persiste a escolha na conta para a API responder no mesmo idioma (e-mails,
    // mensagens de erro, OTP) e o idioma seguir o usuário entre dispositivos.
    // Fire-and-forget: se não houver sessão ainda, ignora silenciosamente.
    void apiFetch('PATCH', '/me', { locale: l }).catch(() => {});
  };

  const t = useMemo(() => {
    const base = createTranslator(locale);
    return (key: string, values?: MessageValues) => base(`admin.${key}`, values);
  }, [locale]);

  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  return <Ctx.Provider value={{ locale, setLocale, t }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useI18n fora do I18nProvider');
  return c;
}

export function useT() {
  return useI18n().t;
}

/** Marca que o usuário ainda não escolheu idioma manualmente (pra sync com /me). */
export function hasManualLocale(): boolean {
  return typeof localStorage !== 'undefined' && localStorage.getItem(KEY) != null;
}
